import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { localizedPath } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Share2, Copy, MessageCircle, Instagram, FileImage, Link as LinkIcon, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareSheet — sharing + PDF (with premium modes) for a completed result.
 * Modes (pdf_config.mode via /api/public/results/{code}/pdf-mode):
 *   free        → direct PDF download
 *   email_gated → user enters email; PDF sent by email (or request queued)
 *   premium     → locked; UI shows email-request CTA
 *   paid        → email delivery is available only after backend entitlement
 */
export default function ShareSheet({ code, instrument, primary, lang = "id", publicShareEnabled = false, compact = false, accessToken = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [tokenPath, setTokenPath] = useState("");
  const [pdfMode, setPdfMode] = useState({ mode: "free", email_configured: false });
  const [emailForm, setEmailForm] = useState({ show: false, value: "", sending: false });

  useEffect(() => {
    if (!open) return;
    api.get(`/public/results/${code}/pdf-mode`).then((r) => setPdfMode(r.data)).catch(() => {});
  }, [open, code]);

  const instName = (lang === "en" ? instrument?.name_en : instrument?.name_id) || instrument?.name_id || "Mindovia";
  const primaryName = primary ? ((lang === "en" ? primary.name_en : primary.name_id) || primary.name_id) : "";

  // Friends get a teaser of this result that invites them to take the test, never the full report.
  // With the instrument's public link on, that is /share/{token} (via the OG page so chat apps preview
  // the result card); otherwise the link goes straight to the instrument page.
  const instrumentUrl = `${window.location.origin}${localizedPath(`/instruments/${instrument?.slug || ""}`, lang)}`;
  const ensureShareToken = async () => {
    if (!publicShareEnabled || !code) return instrumentUrl;
    if (tokenPath) return `${window.location.origin}${tokenPath}`;
    try {
      const { data } = await api.post(`/public/results/${code}/share`, {});
      const path = `/api/og/share/${data.token}`;
      setTokenPath(path);
      return `${window.location.origin}${path}`;
    } catch {
      return instrumentUrl;
    }
  };

  const getCard = async (fmt) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/results/${code}/social-card?fmt=${fmt}&identity=${(instrument?.share_config?.identity_display) || "none"}&lang=${lang}`);
      return `${window.location.origin}${data.url}`;
    } finally { setBusy(false); }
  };

  const shareWA = async () => {
    setBusy(true);
    const url = await ensureShareToken();
    const caption = lang === "id"
      ? `Aku baru selesai ikut "${instName}" di Mindovia, dan hasilku: ${primaryName}. Kamu juga coba, yuk: ${url}`
      : `I just took "${instName}" on Mindovia and got ${primaryName}. Try it yourself: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener");
    setShareUrl(url); setBusy(false);
  };

  const saveImage = async (fmt) => {
    const cardUrl = await getCard(fmt);
    const a = document.createElement("a");
    a.href = cardUrl; a.download = `mindovia-${code}-${fmt}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(lang === "id" ? "Gambar diunduh" : "Image downloaded");
  };

  const shareIGStory = async () => { await saveImage("story"); toast.info(lang === "id" ? "Buka Instagram, buat Story, lalu pilih gambarnya dari galeri." : "Open Instagram, start a Story, and pick the image from your gallery."); };
  const shareIGFeed  = async () => { await saveImage("feed");  toast.info(lang === "id" ? "Buka Instagram, buat postingan, lalu pilih gambarnya dari galeri."  : "Open Instagram, start a new post, and pick the image from your gallery."); };

  const copyLink = async () => {
    const url = await ensureShareToken();
    setShareUrl(url);
    try { await navigator.clipboard.writeText(url); toast.success(lang === "id" ? "Tautan disalin" : "Link copied"); }
    catch { toast.error("Couldn't copy the link"); }
  };
  const nativeShare = async () => {
    const url = await ensureShareToken(); setShareUrl(url);
    if (navigator.share) { try { await navigator.share({ title: instName, text: `${instName} — ${primaryName}`, url }); } catch {} }
    else copyLink();
  };

  const requestPdfByEmail = async () => {
    if (!emailForm.value || !/@/.test(emailForm.value)) { toast.error(lang === "id" ? "Email tidak valid" : "Invalid email"); return; }
    setEmailForm((p) => ({ ...p, sending: true }));
    try {
      const { data } = await api.post(`/public/results/${code}/email-pdf`, {
        email: emailForm.value,
        ...(accessToken ? { token: accessToken } : {}),
      }, { params: { lang } });
      if (data.ok) toast.success(lang === "id" ? "PDF terkirim ke emailmu" : "PDF sent to your email");
      else toast(lang === "id" ? "Permintaanmu tersimpan. Layanan email belum aktif, jadi tim kami akan mengirimkannya secara manual." : "Request saved. Email isn't set up yet, so our team will send it to you manually.");
      setEmailForm({ show: false, value: "", sending: false });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); setEmailForm((p) => ({ ...p, sending: false })); }
  };

  // A temporarily unavailable gateway must not make a configured paid report
  // look free. The result payload's sanitized pdf_config remains authoritative
  // for the lock state; /pdf-mode supplies checkout availability and labels.
  const mode = instrument?.pdf_config?.mode === "paid" ? "paid" : (pdfMode.mode || "free");
  const pdfLocked = mode !== "free";
  const paidWithoutAccess = mode === "paid" && !accessToken;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {compact ? (
          <Button data-testid="open-share-sheet-btn" variant="outline" size="icon" className="rounded-full" aria-label={lang === "id" ? "Bagikan hasil" : "Share result"}>
            <Share2 className="w-4 h-4" />
          </Button>
        ) : (
          <Button data-testid="open-share-sheet-btn" className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
            <Share2 className="w-4 h-4 mr-2" /> {lang === "id" ? "Bagikan Hasil" : "Share Result"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="share-sheet">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{lang === "id" ? "Bagikan Hasil" : "Share Your Result"}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {lang === "id" ? "Gambar dibuat dari hasilmu dan tidak memuat informasi pribadi." : "Images are made from your result and don't include any personal information."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Button variant="outline" className="justify-start" onClick={shareWA} disabled={busy} data-testid="share-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> WhatsApp</Button>
          <Button variant="outline" className="justify-start" onClick={shareIGStory} disabled={busy} data-testid="share-ig-story-btn"><Instagram className="w-4 h-4 mr-2 text-pink-500" /> IG Story</Button>
          <Button variant="outline" className="justify-start" onClick={shareIGFeed} disabled={busy} data-testid="share-ig-feed-btn"><Instagram className="w-4 h-4 mr-2 text-pink-500" /> IG Feed</Button>
          <Button variant="outline" className="justify-start" onClick={() => saveImage("feed")} disabled={busy} data-testid="share-save-image-btn"><FileImage className="w-4 h-4 mr-2 text-teal-700" /> {lang === "id" ? "Simpan Gambar" : "Save Image"}</Button>
          <Button variant="outline" className="justify-start" onClick={copyLink} disabled={busy} data-testid="share-copy-link-btn"><LinkIcon className="w-4 h-4 mr-2" /> {lang === "id" ? "Salin Tautan" : "Copy Link"}</Button>
          <Button variant="outline" className="justify-start" onClick={nativeShare} disabled={busy} data-testid="share-native-generic-btn"><Share2 className="w-4 h-4 mr-2" /> {lang === "id" ? "Bagikan…" : "Share…"}</Button>
          {pdfLocked && (
            <div className="col-span-2 p-3 rounded-xl border border-dashed border-terracotta-200 bg-terracotta-50/60" data-testid="share-pdf-gated">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-terracotta-700 mt-0.5" />
                <div className="text-xs text-teal-800 leading-relaxed" data-wrap="responsive">
                  <b>{mode === "email_gated" ? (lang === "id" ? "Terima PDF via email" : "Get the PDF via email") : paidWithoutAccess ? (lang === "id" ? "Laporan lengkap terkunci" : "Full report locked") : (lang === "id" ? "Kirim laporan ke email" : "Email the full report")}</b>
                  <div className="mt-1 text-muted-foreground">
                    {paidWithoutAccess
                      ? (lang === "id" ? "Selesaikan pembayaran dari halaman hasil sebelum meminta PDF melalui email." : "Complete payment from the result page before requesting the PDF by email.")
                      : (lang === "id" ? "Masukkan emailmu untuk menerima PDF laporan lengkap." : "Enter your email to receive the full PDF report.")}
                  </div>
                </div>
              </div>
              {!paidWithoutAccess && (!emailForm.show ? (
                <Button size="sm" className="mt-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" onClick={() => setEmailForm({ show: true, value: "", sending: false })} data-testid="share-pdf-email-open"><Mail className="w-3.5 h-3.5 mr-1.5" /> {lang === "id" ? "Kirim ke email saya" : "Email me the PDF"}</Button>
              ) : (
                <div className="mt-2 flex gap-2 items-center">
                  <input type="email" value={emailForm.value} onChange={(e) => setEmailForm((p) => ({ ...p, value: e.target.value }))} placeholder="you@example.com" className="flex-1 h-9 rounded-full px-4 border border-border bg-background text-sm" data-testid="share-pdf-email-input" />
                  <Button size="sm" onClick={requestPdfByEmail} disabled={emailForm.sending} className="rounded-full bg-teal-800 hover:bg-teal-700 text-cream-50" data-testid="share-pdf-email-send">{emailForm.sending ? "…" : (lang === "id" ? "Kirim" : "Send")}</Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 p-3 rounded-xl bg-cream-100 border border-border text-xs text-muted-foreground" data-wrap="responsive" data-testid="share-link-note">
          {publicShareEnabled
            ? (lang === "id"
              ? "Tautan ini menampilkan kartu hasilmu (tanpa data pribadi) dan mengajak temanmu mencoba tes ini."
              : "The link shows your result card (no personal data) and invites friends to take the test too.")
            : (lang === "id"
              ? "Tautan ini mengajak temanmu mencoba tes ini. Hasilmu tidak ikut terlihat."
              : "The link invites friends to take the test. Your result stays private.")}
        </div>
        {shareUrl && (
          <div className="mt-4 p-3 rounded-xl bg-secondary text-xs break-all" data-testid="share-sheet-url">{shareUrl}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
